import { useState, useEffect } from "react";
import { Bell, BellOff, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush
} from "@/utils/pushNotifications";

export default function PushNotificationBanner() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      // Check if already dismissed
      const wasDismissed = localStorage.getItem('push_banner_dismissed');
      if (wasDismissed) {
        setDismissed(true);
        return;
      }

      const supported = isPushSupported();
      setIsSupported(supported);
      
      if (supported) {
        const perm = getNotificationPermission();
        setPermission(perm);
        
        const subscribed = await isSubscribedToPush();
        setIsSubscribed(subscribed);
        
        // Show banner if supported and not yet subscribed
        if (!subscribed && perm !== 'denied') {
          // Delay showing banner for better UX
          setTimeout(() => setShowBanner(true), 3000);
        }
      }
    };

    checkStatus();
  }, []);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      setIsSubscribed(true);
      setShowBanner(false);
      toast.success("¡Notificaciones activadas! Te avisaremos cuando haya partidos en vivo.");
    } catch (error) {
      console.error('Subscribe error:', error);
      if (error.message.includes('denegado')) {
        toast.error("Permiso denegado. Activa las notificaciones en la configuración del navegador.");
        setPermission('denied');
      } else {
        toast.error(error.message || "Error al activar notificaciones");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('push_banner_dismissed', 'true');
  };

  // Don't render if not supported, already subscribed, or dismissed
  if (!isSupported || isSubscribed || dismissed || permission === 'denied' || !showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-gradient-to-r from-primary to-green-600 rounded-xl p-4 shadow-xl text-white">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <Bell className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm mb-1">¡No te pierdas ningún partido!</h3>
            <p className="text-xs text-white/90 mb-3">
              Activa las notificaciones para saber cuando un partido comienza EN VIVO.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                size="sm"
                className="bg-white text-primary hover:bg-white/90 flex-1"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Bell className="h-4 w-4 mr-1" />
                    Activar
                  </>
                )}
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                Ahora no
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mini button version for header/settings
export function PushNotificationToggle() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      const supported = isPushSupported();
      setIsSupported(supported);
      
      if (supported) {
        const subscribed = await isSubscribedToPush();
        setIsSubscribed(subscribed);
      }
    };

    checkStatus();
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (isSubscribed) {
        await unsubscribeFromPush();
        setIsSubscribed(false);
        toast.success("Notificaciones desactivadas");
      } else {
        await subscribeToPush();
        setIsSubscribed(true);
        toast.success("Notificaciones activadas");
      }
    } catch (error) {
      toast.error(error.message || "Error al cambiar notificaciones");
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) return null;

  return (
    <Button
      onClick={handleToggle}
      disabled={loading}
      variant={isSubscribed ? "default" : "outline"}
      size="sm"
      className="gap-2"
      title={isSubscribed ? "Desactivar notificaciones" : "Activar notificaciones"}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isSubscribed ? (
        <>
          <Bell className="h-4 w-4" />
          <span className="hidden sm:inline">Notificaciones ON</span>
        </>
      ) : (
        <>
          <BellOff className="h-4 w-4" />
          <span className="hidden sm:inline">Notificaciones</span>
        </>
      )}
    </Button>
  );
}
